import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Collapse,
  Descriptions,
  Empty,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';

import { apiClient } from '../../../helpers/apiHelper';
import type {
  BsExamAnalyticsBestFrame,
  BsExamAnalyticsDetector,
  BsExamAnalyticsResponse,
  BsExamAnalyticsSweepSummary,
} from '../../../helpers/api/blind-sweep/bs-analytics';

const { Title, Text, Paragraph } = Typography;

interface BsExamListItem {
  study_instance_uid: string;
  study_date: string | null;
  created_at: string | null;
  serie_instance_uid: string;
  has_analytics: boolean;
  summary: string | null;
  analytics_updated_at: string | null;
  autoselect_status: string | null;
  rules_execution_status: string | null;
}

const STAGE_ORDER = ['autoselect', 'rules_enqueue', 'rules_execution'] as const;
const STAGE_LABELS: Record<(typeof STAGE_ORDER)[number], string> = {
  autoselect: 'Report autoselect',
  rules_enqueue: 'Rules enqueue',
  rules_execution: 'Rules execution',
};

function formatDetector(detector: BsExamAnalyticsDetector): string {
  const result = detector.result || {};
  switch (detector.name) {
    case 'presentation':
      return `presentation: ${result.presentation ?? '?'} | facing: ${result.facing ?? '?'}`;
    case 'placenta':
      return `location: ${result.location ?? '?'}`;
    case 'multiple_pregnancy':
      return `type: ${result.pregnancy_type ?? '?'}`;
    case 'cardiac_activity':
      return `detected: ${String(result.detected ?? false)}`;
    default:
      return JSON.stringify(result);
  }
}

async function fetchExamList(): Promise<BsExamListItem[]> {
  const response = await apiClient.get('/blind-sweep/analytics/exams');
  // apiClient interceptor already unwraps axios response.data
  return response?.data?.items ?? [];
}

async function fetchAnalytics(
  serieUid: string
): Promise<BsExamAnalyticsResponse | null> {
  try {
    const response = await apiClient.get(
      `/blind-sweep/analytics/series/${encodeURIComponent(serieUid)}`
    );
    // apiClient interceptor already unwraps axios response.data
    return response?.data ?? null;
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

function BsExamAnalyticsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedFromUrl = searchParams.get('SeriesInstanceUID');

  const [exams, setExams] = useState<BsExamListItem[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  const [selectedSerieUid, setSelectedSerieUid] = useState<string | null>(
    selectedFromUrl
  );
  const [detail, setDetail] = useState<BsExamAnalyticsResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const loadList = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      const items = await fetchExamList();
      setExams(items);
    } catch (err: any) {
      setListError(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to load blind sweep exams'
      );
    } finally {
      setListLoading(false);
    }
  }, []);

  const loadDetail = useCallback(async (serieUid: string) => {
    setDetailLoading(true);
    setDetailError(null);
    setSelectedSerieUid(serieUid);
    setSearchParams({ SeriesInstanceUID: serieUid });
    try {
      const result = await fetchAnalytics(serieUid);
      setDetail(result);
      if (!result) {
        setDetailError(
          'No analytics row yet for this series. Re-run prediction after restarting backend with BS_EXAM_ANALYTICS=true.'
        );
      }
    } catch (err: any) {
      setDetail(null);
      setDetailError(err?.response?.data?.message || err?.message || 'Failed to load detail');
    } finally {
      setDetailLoading(false);
    }
  }, [setSearchParams]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    if (selectedFromUrl) {
      loadDetail(selectedFromUrl);
    }
  }, [selectedFromUrl, loadDetail]);

  useEffect(() => {
    if (!selectedSerieUid) {
      return;
    }
    const rulesStatus = detail?.payload?.stages?.rules_execution?.status;
    if (rulesStatus === 'completed' || rulesStatus === 'failed') {
      return;
    }
    const timer = window.setInterval(() => {
      fetchAnalytics(selectedSerieUid).then((next) => {
        if (next) {
          setDetail(next);
          setDetailError(null);
        }
      });
    }, 5000);
    return () => window.clearInterval(timer);
  }, [selectedSerieUid, detail?.payload?.stages?.rules_execution?.status]);

  const listColumns: ColumnsType<BsExamListItem> = useMemo(
    () => [
      {
        title: 'Study date',
        dataIndex: 'study_date',
        key: 'study_date',
        width: 120,
        render: (v: string | null) =>
          v ? new Date(v).toLocaleDateString() : '—',
      },
      {
        title: 'Study UID',
        dataIndex: 'study_instance_uid',
        key: 'study_instance_uid',
        ellipsis: true,
        render: (v: string) => (
          <Text code className="text-xs">
            …{v.slice(-12)}
          </Text>
        ),
      },
      {
        title: 'Analytics',
        key: 'has_analytics',
        width: 100,
        render: (_: unknown, row) =>
          row.has_analytics ? (
            <Tag color="green">yes</Tag>
          ) : (
            <Tag>pending</Tag>
          ),
      },
      {
        title: 'Autoselect',
        dataIndex: 'autoselect_status',
        key: 'autoselect_status',
        width: 110,
        render: (v: string | null) => v || '—',
      },
      {
        title: 'Rules',
        dataIndex: 'rules_execution_status',
        key: 'rules_execution_status',
        width: 100,
        render: (v: string | null) => v || '—',
      },
      {
        title: 'Summary',
        dataIndex: 'summary',
        key: 'summary',
        ellipsis: true,
        render: (v: string | null) => v || '—',
      },
      {
        title: '',
        key: 'action',
        width: 90,
        render: (_: unknown, row) => (
          <Button
            type="link"
            size="small"
            onClick={() => loadDetail(row.serie_instance_uid)}
          >
            View
          </Button>
        ),
      },
    ],
    [loadDetail]
  );

  const stages = detail?.payload?.stages;
  const presentation = stages?.rules_execution?.presentation_result as
    | Record<string, any>
    | undefined;

  const frameColumns: ColumnsType<BsExamAnalyticsBestFrame> = [
    { title: 'View', dataIndex: 'view_code', key: 'view_code' },
    { title: 'Frame #', dataIndex: 'frame_number', key: 'frame_number', width: 80 },
    {
      title: 'Frame ID',
      dataIndex: 'frame_id',
      key: 'frame_id',
      render: (v: string) => <Text code className="text-xs">{v}</Text>,
    },
    { title: 'Quality %', dataIndex: 'quality_percentage', key: 'quality_percentage', width: 90 },
  ];

  const sweepColumns: ColumnsType<BsExamAnalyticsSweepSummary> = [
    { title: 'Sweep', dataIndex: 'sweep_index', key: 'sweep_index', width: 70 },
    { title: 'Frames', dataIndex: 'frame_count', key: 'frame_count', width: 80 },
    {
      title: 'w/ structures',
      dataIndex: 'frames_with_structures',
      key: 'frames_with_structures',
      width: 110,
    },
    { title: 'Detections', dataIndex: 'total_detections', key: 'total_detections', width: 100 },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <Title level={2} className="!mb-1">
              BS Exam Analytics
            </Title>
            <Text type="secondary">
              Blind sweep studies and pipeline analytics (all clinical users).
            </Text>
          </div>
          <Button onClick={loadList} loading={listLoading}>
            Refresh list
          </Button>
        </div>

        {listError ? <Alert type="error" showIcon message={listError} /> : null}

        <Card title="Blind sweep studies">
          <Table
            size="small"
            rowKey="serie_instance_uid"
            loading={listLoading}
            columns={listColumns}
            dataSource={exams}
            pagination={{ pageSize: 15 }}
            locale={{ emptyText: 'No blind sweep studies or analytics rows yet' }}
            onRow={(row) => ({
              onClick: () => loadDetail(row.serie_instance_uid),
              className: 'cursor-pointer',
            })}
            rowClassName={(row) =>
              row.serie_instance_uid === selectedSerieUid ? 'bg-blue-50' : ''
            }
          />
        </Card>

        {selectedSerieUid ? (
          <Card
            title={`Pipeline detail — …${selectedSerieUid.slice(-12)}`}
            extra={
              <Button size="small" onClick={() => loadDetail(selectedSerieUid)}>
                Reload
              </Button>
            }
          >
            {detailLoading && !detail ? (
              <div className="flex justify-center py-12">
                <Spin />
              </div>
            ) : null}

            {detailError ? <Alert type="warning" showIcon message={detailError} className="mb-4" /> : null}

            {detail ? (
              <div className="space-y-4">
                {detail.payload?.summary ? (
                  <Paragraph className="mb-0">
                    <Text strong>Summary: </Text>
                    {detail.payload.summary}
                  </Paragraph>
                ) : null}

                <Descriptions size="small" column={1} bordered>
                  <Descriptions.Item label="Study">
                    <Text code className="text-xs">{detail.study_instance_uid}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Series">
                    <Text code className="text-xs">{detail.serie_instance_uid}</Text>
                  </Descriptions.Item>
                  {detail.updated_at ? (
                    <Descriptions.Item label="Updated">
                      {new Date(detail.updated_at).toLocaleString()}
                    </Descriptions.Item>
                  ) : null}
                </Descriptions>

                <div className="grid gap-4 md:grid-cols-3">
                  {STAGE_ORDER.map((key) => {
                    const stage = stages?.[key];
                    const status = stage?.status || 'pending';
                    return (
                      <Card key={key} size="small" title={STAGE_LABELS[key]}>
                        <Tag
                          color={
                            status === 'failed'
                              ? 'red'
                              : status === 'pending'
                                ? 'default'
                                : 'green'
                          }
                        >
                          {status}
                        </Tag>
                      </Card>
                    );
                  })}
                </div>

                {presentation ? (
                  <Card size="small" title="Findings">
                    <Descriptions size="small" column={2}>
                      <Descriptions.Item label="Presentation">
                        {presentation.fetal_presentation?.label ?? '—'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Placenta">
                        {presentation.placenta?.location ?? '—'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Twins">
                        {presentation.multiple_pregnancy?.pregnancy_type ?? '—'}
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>
                ) : null}

                <Card size="small" title="Report best frames">
                  <Table
                    size="small"
                    rowKey={(r) => `${r.view_code}-${r.frame_id}`}
                    columns={frameColumns}
                    dataSource={stages?.autoselect?.best_report_frames || []}
                    pagination={false}
                    locale={{ emptyText: 'None' }}
                  />
                </Card>

                <Card size="small" title="Rule engine sweep inputs">
                  <Table
                    size="small"
                    rowKey="sweep_index"
                    columns={sweepColumns}
                    dataSource={stages?.rules_enqueue?.sweep_input_summary || []}
                    pagination={false}
                    locale={{ emptyText: 'None' }}
                  />
                </Card>

                <Card size="small" title="Detectors">
                  {(stages?.rules_execution?.detectors || []).length === 0 ? (
                    <Empty description="Waiting for rules" />
                  ) : (
                    <div className="space-y-2">
                      {(stages?.rules_execution?.detectors || []).map((detector) => (
                        <div key={detector.name}>
                          <Text strong>{detector.name}: </Text>
                          <Text>{formatDetector(detector)}</Text>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>

                <Collapse
                  items={[
                    {
                      key: 'raw',
                      label: 'Raw JSON',
                      children: (
                        <pre className="max-h-96 overflow-auto rounded bg-gray-100 p-3 text-xs">
                          {JSON.stringify(detail, null, 2)}
                        </pre>
                      ),
                    },
                  ]}
                />
              </div>
            ) : !detailLoading ? (
              <Empty description="Select a study with analytics or re-run prediction" />
            ) : null}
          </Card>
        ) : null}
      </div>
    </div>
  );
}

export default BsExamAnalyticsPage;
